import { useEffect, useState } from "react";
import { Timestamp } from "firebase/firestore";
import { clearPersistedCache, readPersistedCache, writePersistedCache } from "./persistedCache";

/**
 * ONE LIVE FIRESTORE LISTENER PER ACCOUNT, SHARED BY EVERY SCREEN, REMEMBERED ACROSS A RELOAD.
 *
 * Built for `/dashboard/whatsapp`, which drew nothing but a spinner until two tiny listeners
 * answered (the user's numbers, ≤3 docs; their own connection status, one ~2KB doc). Measured
 * 2026-10-15 from a real Chrome with this app's transport settings: ~0.6-1.1s for the pair on a
 * warm connection, 1.7-2.9s for the first listen of a session. And the page paid it on EVERY
 * open, including each time the realtor clicked back to the "חיבור" tab, because each listener
 * lived inside the component that mounted it: unmount released the target, the next mount
 * asked Firestore again. This app never calls `enablePersistence`, so nothing on the device
 * remembered the answer in between.
 *
 * Same cure as `useLocationGroups` / `useOrgDirectory`:
 *  - the listener is MODULE-level, opened by the first reader and deliberately left running when
 *    readers unmount (a doc or two per account, for the session), so returning is instant;
 *  - the last SUCCESSFUL snapshot is kept in `persistedCache` (account-stamped, expiring) and is
 *    the first render's value after a reload, with the live listener correcting it a moment later.
 *
 * ⚠️ What a store holds is a DISPLAY value, never an access decision — the rules and the server
 * decide every read and write whatever the cache says. A stale copy can show a status for the
 * moment before the listener corrects it; it cannot grant anything.
 *
 * A failure is never persisted, and never replaces a value already held: Firestore ends a
 * listener on its first error, so the store forgets it and the next reader to mount opens a new one.
 */

export type AccountLiveSnapshot<T> = {
  /** The held answer; `undefined` until one is known. */
  value: T | undefined;
  /** A live snapshot, a persisted copy or a failure has arrived — a spinner may end. */
  settled: boolean;
};

export type AccountLivePersistence<T> = {
  /** An envelope, because `null` can be a remembered answer ("never connected") and not just a miss. */
  read: (account: string) => { value: T } | null;
  write: (account: string, value: T) => void;
  clear: () => void;
};

export type AccountLiveStoreOptions<T> = {
  /** Named in the console when the listener fails. */
  label: string;
  /** Opens the listener and returns its unsubscribe. Called again after a failure. */
  listen: (account: string, onValue: (value: T) => void, onError: (error: unknown) => void) => () => void;
  /** Where the last good snapshot is kept. */
  persistence: AccountLivePersistence<T>;
};

export type AccountLiveStore<T> = {
  /** Idempotent. Starts the listener for `account` (or re-opens a failed one); a change of account drops everything. */
  bind: (account: string | null) => void;
  read: () => AccountLiveSnapshot<T>;
  /** What `account`'s first render can show, without starting anything: the held snapshot, else the persisted one. */
  peek: (account: string | null) => AccountLiveSnapshot<T>;
  subscribe: (listener: () => void) => () => void;
};

const UNKNOWN: AccountLiveSnapshot<undefined> = { value: undefined, settled: false };

/**
 * `JSON.stringify` writes a Firestore `Timestamp` as `{ seconds, nanoseconds }` and reads it back
 * as a plain object, which `toJsDate` (and anything calling `.toDate()`) does not recognise — the
 * connection card would say "נבדק" without a date for the frame before the listener lands. This
 * puts the persisted copy back into the shape the live listener hands out.
 */
export const reviveFirestoreTimestamps = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map((item) => reviveFirestoreTimestamps(item)) as unknown as T;
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (
    keys.length === 2 &&
    typeof record.seconds === "number" &&
    typeof record.nanoseconds === "number"
  ) {
    return new Timestamp(record.seconds, record.nanoseconds) as unknown as T;
  }
  const revived: Record<string, unknown> = {};
  keys.forEach((key) => {
    revived[key] = reviveFirestoreTimestamps(record[key]);
  });
  return revived as T;
};

/** `persistedCache` under `key`, with Timestamps revived on the way out. */
export const persistedCacheFor = <T>(key: string): AccountLivePersistence<T> => ({
  read: (account) => {
    const persisted = readPersistedCache<T>(key, account);
    return persisted ? { value: reviveFirestoreTimestamps(persisted.value) } : null;
  },
  write: (account, value) => writePersistedCache(key, account, value),
  clear: () => clearPersistedCache(key)
});

export function createAccountLiveStore<T>({ label, listen, persistence }: AccountLiveStoreOptions<T>): AccountLiveStore<T> {
  let bound: string | null = null;
  let release: (() => void) | null = null;
  let snapshot: AccountLiveSnapshot<T> = UNKNOWN;
  const listeners = new Set<() => void>();

  const publish = (next: AccountLiveSnapshot<T>): void => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const open = (account: string): void => {
    release = listen(
      account,
      (value) => {
        if (bound !== account) return;
        persistence.write(account, value);
        publish({ value, settled: true });
      },
      (error) => {
        if (bound !== account) return;
        console.error(`${label} listener failed:`, error);
        release = null;
        publish({ value: snapshot.value, settled: true });
      }
    );
  };

  const fromStorage = (account: string): AccountLiveSnapshot<T> => {
    const persisted = persistence.read(account);
    return persisted === null ? UNKNOWN : { value: persisted.value, settled: true };
  };

  return {
    bind: (account) => {
      if (account === bound) {
        if (account && !release) open(account);
        return;
      }
      release?.();
      release = null;
      bound = account;
      if (!account) {
        // Signed out: the persisted copy goes too. The account stamp would refuse it to the next
        // person anyway; this is the belt.
        persistence.clear();
        publish(UNKNOWN);
        return;
      }
      publish(fromStorage(account));
      open(account);
    },
    read: () => snapshot,
    peek: (account) => {
      if (!account) return UNKNOWN;
      return account === bound ? snapshot : fromStorage(account);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

/**
 * Reads a store for `account`, binding it on mount. Unmounting stops only THIS reader — the
 * listener stays up for the next one (see the header).
 */
export function useAccountLiveStore<T>(store: AccountLiveStore<T>, account: string | null): AccountLiveSnapshot<T> {
  const [state, setState] = useState(() => ({ account, snapshot: store.peek(account) }));

  useEffect(() => {
    const sync = () => setState({ account, snapshot: store.read() });
    const unsubscribe = store.subscribe(sync);
    store.bind(account);
    sync();
    return unsubscribe;
  }, [store, account]);

  // An account switch renders once before the effect re-binds; never show it the previous account's value.
  return state.account === account ? state.snapshot : store.peek(account);
}
